/**
 * Bouton OAuth Google - "Continuer avec Google"
 * Charge le SDK uniquement au premier clic pour éviter les erreurs en console
 */
import { useState, useRef } from 'react';

interface GoogleOAuthButtonProps {
  onSuccess: (credential: string) => void;
  onError?: (error: Error) => void;
  disabled?: boolean;
}

export function GoogleOAuthButton({ onSuccess, onError, disabled }: GoogleOAuthButtonProps) {
  const [isInitializing, setIsInitializing] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const buttonContainerRef = useRef<HTMLDivElement>(null);
  const customButtonRef = useRef<HTMLButtonElement>(null);

  const handleClick = async () => {
    if (disabled || isInitializing || isInitialized) return;
    
    setIsInitializing(true);
    
    try {
      // Charger le SDK Google si pas déjà chargé
      if (!window.google) {
        const script = document.createElement('script');
        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);
        
        await new Promise((resolve) => {
          script.onload = resolve;
        });
      }

      // Obtenir le client ID depuis les variables d'environnement
      const clientId = typeof import.meta !== 'undefined' && import.meta.env 
        ? import.meta.env.VITE_GOOGLE_CLIENT_ID 
        : undefined;
      
      if (!clientId) {
        console.warn('Google Client ID non configuré - OAuth Google désactivé');
        throw new Error('Google Client ID non configuré');
      }

      // Initialiser Google Sign-In
      if (!window.google) {
        throw new Error('Google SDK not loaded');
      }
      
      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: (response: { credential?: string }) => {
          if (response.credential) {
            onSuccess(response.credential);
          }
        },
      });

      setIsInitialized(true);
      
      // Rendre le bouton Google natif à la place du bouton custom
      if (buttonContainerRef.current) {
        // Masquer le bouton custom
        if (customButtonRef.current) {
          customButtonRef.current.style.display = 'none';
        }
        
        // Créer un div pour le bouton Google
        const googleButtonDiv = document.createElement('div');
        googleButtonDiv.id = 'google-signin-button';
        buttonContainerRef.current.appendChild(googleButtonDiv);
        
        // Rendre le bouton Google
        if (!window.google) {
          throw new Error('Google SDK not loaded');
        }
        
        window.google.accounts.id.renderButton(
          googleButtonDiv,
          { 
            theme: 'outline', 
            size: 'large',
          } as { theme: string; size: string }
        );
      }

    } catch (error) {
      console.error('Erreur Google OAuth:', error);
      if (onError) {
        onError(error as Error);
      }
      setIsInitializing(false);
    }
  };

  return (
    <div ref={buttonContainerRef} className="w-full">
      <button
        ref={customButtonRef}
        onClick={handleClick}
        disabled={disabled || isInitializing}
        className="w-full flex items-center justify-center gap-3 px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <svg className="w-5 h-5" viewBox="0 0 24 24">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
        </svg>
        <span className="text-gray-700 font-medium">
          {isInitializing ? 'Chargement...' : 'Continuer avec Google'}
        </span>
      </button>
    </div>
  );
}

/**
 * Bouton OAuth Apple - "Continuer avec Apple"
 */
interface AppleOAuthButtonProps {
  onSuccess: (data: { id_token: string; user?: Record<string, unknown> }) => void;
  onError?: (error: Error) => void;
  disabled?: boolean;
}

export function AppleOAuthButton({ onSuccess, onError, disabled }: AppleOAuthButtonProps) {
  const handleAppleLogin = async () => {
    try {
      // Charger le SDK Apple si pas déjà chargé
      if (!window.AppleID) {
        const script = document.createElement('script');
        script.src = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
        script.async = true;
        document.head.appendChild(script);
        
        await new Promise((resolve) => {
          script.onload = resolve;
        });
      }

      // Obtenir la config depuis les variables d'environnement
      const clientId = import.meta.env.VITE_APPLE_CLIENT_ID;
      const redirectURI = import.meta.env.VITE_APPLE_REDIRECT_URI || window.location.origin;
      
      if (!clientId) {
        throw new Error('Apple Client ID non configuré');
      }

      // Initialiser Apple Sign-In
      if (!window.AppleID) {
        throw new Error('Apple SDK not loaded');
      }
      
      window.AppleID.auth.init({
        clientId,
        scope: 'name email',
        redirectURI,
        usePopup: true,
      });

      // Déclencher la connexion
      if (!window.AppleID) {
        throw new Error('Apple SDK not loaded');
      }
      
      const response = await window.AppleID.auth.signIn();
      
      if (response.authorization) {
        onSuccess({
          id_token: response.authorization.id_token,
          user: response.user, // firstName, lastName (uniquement première connexion)
        });
      }

    } catch (error) {
      console.error('Erreur Apple OAuth:', error);
      if (onError) {
        onError(error instanceof Error ? error : new Error(String(error)));
      }
    }
  };

  return (
    <button
      onClick={handleAppleLogin}
      disabled={disabled}
      className="w-full flex items-center justify-center gap-3 px-4 py-2 border border-gray-900 dark:border-white rounded-md shadow-sm bg-black dark:bg-white text-white dark:text-black hover:bg-gray-900 dark:hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
    >
      {/* Logo Apple */}
      <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24">
        <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
      </svg>
      <span className="font-medium">Continuer avec Apple</span>
    </button>
  );
}

/**
 * Divider "OU" pour séparer les méthodes OAuth et email/password
 */
export function OAuthDivider() {
  return (
    <div className="relative my-6">
      <div className="absolute inset-0 flex items-center">
        <div className="w-full border-t border-gray-300 dark:border-gray-600"></div>
      </div>
      <div className="relative flex justify-center text-sm">
        <span className="px-2 bg-gray-50 dark:bg-gray-900 text-gray-500 dark:text-gray-400">
          OU
        </span>
      </div>
    </div>
  );
}

// Types globaux pour les SDK
declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: { client_id: string; callback: (response: { credential?: string }) => void }) => void;
          prompt: (callback: (notification: { isNotDisplayed: () => boolean; isSkippedMoment: () => boolean }) => void) => void;
          renderButton: (element: HTMLElement | null, options: { theme: string; size: string }) => void;
        };
      };
    };
    AppleID?: {
      auth: {
        init: (config: { clientId: string; scope: string; redirectURI: string; usePopup: boolean }) => void;
        signIn: () => Promise<{ authorization: { id_token: string }; user?: Record<string, unknown> }>;
      };
    };
  }
}
